/**
 * AddCableDialog Component - Dialog for adding/editing cables
 */
import React, { useState, useEffect } from 'react';
import { useCableStore, usePoleStore, useProjectStore } from '../../stores';
import { Cable, MicrotubeSize, MicrotubeColor, MicrotubeType } from '../../types';
import { getErrorMessage } from '../../utils/errorMessages';
import { cableTypeFromFiberCount, fiberCountFromCableType } from '../../utils/fiberColors';
import { FiberCountPicker } from '../Common/FiberCountPicker';
import './Dialog.css';

/** Výrazné farby káblov na mape — prvých sedem sedí s legendou trás Svätý Peter. */
const FARBY_KABLA: { hex: string; nazov: string }[] = [
  { hex: '#1f6fd1', nazov: 'Modrá' },
  { hex: '#e8663a', nazov: 'Oranžová' },
  { hex: '#1a9e77', nazov: 'Zelená' },
  { hex: '#f0a202', nazov: 'Žltooranžová' },
  { hex: '#e377c2', nazov: 'Ružová' },
  { hex: '#6a51d6', nazov: 'Fialová' },
  { hex: '#d62728', nazov: 'Červená' },
  { hex: '#17becf', nazov: 'Tyrkysová' },
  { hex: '#8c564b', nazov: 'Hnedá' },
];

interface AddCableDialogProps {
  isOpen: boolean;
  onClose: () => void;
  fromPoleId?: number;
  cable?: Cable | null;
}

export const AddCableDialog: React.FC<AddCableDialogProps> = ({
  isOpen,
  onClose,
  fromPoleId,
  cable,
}) => {
  const { currentProject } = useProjectStore();
  const { poles } = usePoleStore();
  const { createCable, updateCable, selectedCable, setSelectedCable, fetchCables } = useCableStore();

  const [fromPole, setFromPole] = useState<number>(fromPoleId || cable?.from_pole_id || 0);
  const [toPole, setToPole] = useState<number>(cable?.to_pole_id || 0);
  const [length, setLength] = useState<number>(cable?.length_meters || 0);
  const [notes, setNotes] = useState(cable?.notes || '');
  const [microtubeSize, setMicrotubeSize] = useState<MicrotubeSize | ''>(cable?.microtube_size as MicrotubeSize || '');
  const [microtubeColor, setMicrotubeColor] = useState<MicrotubeColor | ''>(cable?.microtube_color as MicrotubeColor || '');
  const [microtubeType, setMicrotubeType] = useState<MicrotubeType | ''>(cable?.microtube_type as MicrotubeType || '');
  // Farba kábla na mape — len pre orientáciu v projekte; '' = predvolená tmavá
  const [mapColor, setMapColor] = useState<string>(cable?.map_color || '');
  // Počet vlákien je uložený v cable_type ("24-fiber"). Doplnené 2026-09-30 —
  // predtým sa počet vlákien existujúceho kábla nedal zmeniť vôbec.
  const [fiberCount, setFiberCount] = useState<number>(fiberCountFromCableType(cable?.cable_type));
  const povodnyPocetVlakien = fiberCountFromCableType((cable || selectedCable)?.cable_type);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update form when cable changes
  useEffect(() => {
    if (cable || selectedCable) {
      const c = cable || selectedCable;
      if (c) {
        setFromPole(c.from_pole_id);
        setToPole(c.to_pole_id);
        setLength(c.length_meters);
        setNotes(c.notes || '');
        setMicrotubeSize(c.microtube_size as MicrotubeSize || '');
        setMicrotubeColor(c.microtube_color as MicrotubeColor || '');
        setMicrotubeType(c.microtube_type as MicrotubeType || '');
        setMapColor(c.map_color || '');
        setFiberCount(fiberCountFromCableType(c.cable_type));
      }
    } else if (fromPoleId) {
      setFromPole(fromPoleId);
    }
  }, [cable, selectedCable, fromPoleId]);

  if (!isOpen) return null;

  const calculateDistance = (pole1Id: number, pole2Id: number): number => {
    const p1 = poles.find((p) => p.id === pole1Id);
    const p2 = poles.find((p) => p.id === pole2Id);

    if (!p1 || !p2 || !p1.latitude || !p2.latitude) return 0;

    const lat1 = typeof p1.latitude === 'string' ? parseFloat(p1.latitude) : p1.latitude;
    const lon1 = typeof p1.longitude === 'string' ? parseFloat(p1.longitude as string) : p1.longitude!;
    const lat2 = typeof p2.latitude === 'string' ? parseFloat(p2.latitude) : p2.latitude;
    const lon2 = typeof p2.longitude === 'string' ? parseFloat(p2.longitude as string) : p2.longitude!;

    // Haversine formula
    const R = 6371000; // Earth radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return Math.round(R * c);
  };

  // Návin sa ZÁMERNE nepredvypĺňa — nerobí sa na každom stĺpe. Zadáva sa
  // ručne, pokojne aj dodatočne pri úprave existujúceho kábla.
  const handleFromPoleChange = (poleId: number) => {
    setFromPole(poleId);
    if (poleId && toPole) {
      setLength(calculateDistance(poleId, toPole));
    }
  };

  const handleToPoleChange = (poleId: number) => {
    setToPole(poleId);
    if (fromPole && poleId) {
      setLength(calculateDistance(fromPole, poleId));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentProject) return;

    if (fromPole === toPole) {
      setError('Počiatočný a koncový stĺpik musia byť rôzne');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const cableData: any = {
        from_pole_id: fromPole,
        to_pole_id: toPole,
        cable_type: cableTypeFromFiberCount(fiberCount),
        // Nulovú dĺžku nemá zmysel ukladať — doplníme vzdialenosť z GPS.
        // Pri úprave to zároveň opraví staré káble, ktoré vznikli klikaním
        // a ostali s nulou.
        length_meters: length || calculateDistance(fromPole, toPole) || undefined,
        notes: notes || undefined,
        microtube_size: microtubeSize || undefined,
        microtube_color: microtubeColor || undefined,
        microtube_type: microtubeType || undefined,
        // null (nie undefined) — pri úprave tak „Predvolená“ farbu naozaj zmaže
        map_color: mapColor || null,
      };

      const editingCable = cable || selectedCable;

      console.log('💾 Saving cable. Editing:', !!editingCable, 'Data:', cableData);

      if (editingCable) {
        // Update existing cable using store
        console.log('📝 Updating cable ID:', editingCable.id);
        await updateCable(currentProject.id, editingCable.id, cableData);
        console.log('✅ Cable update complete');
      } else {
        // Create new cable
        console.log('➕ Creating new cable');
        await createCable(currentProject.id, cableData);
        console.log('✅ Cable create complete');
      }

      handleClose();
    } catch (err: any) {
      console.error('❌ Save cable error:', err);
      setError(getErrorMessage(err, 'Nepodarilo sa uložiť kábel.'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFromPole(fromPoleId || 0);
    setToPole(0);
    setLength(0);
    setNotes('');
    setError(null);
    setSelectedCable(null);
    onClose();
  };

  const getPoleLabel = (pole: any) => {
    const typeIcons: Record<string, string> = {
      olt: '🏢',
      manhole: '🕳️',
      splitter_point: '🔀',
      building: '🏠',
      pole: '📍',
    };
    const typeIcon = typeIcons[pole.pole_type] || '📍';
    return `${typeIcon} ${pole.name}`;
  };

  const editingCable = cable || selectedCable;
  const isEditing = !!editingCable;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>{isEditing ? 'Upraviť kábel' : 'Pridať nový kábel'}</h2>
          <button className="dialog-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-content">
          {error && <div className="dialog-error">{error}</div>}

          <div className="form-group">
            <label>Zo stĺpika *</label>
            <select
              value={fromPole}
              onChange={(e) => handleFromPoleChange(parseInt(e.target.value))}
              required
            >
              <option value={0}>Vyberte stĺpik</option>
              {poles.filter(pole => !pole.is_virtual).map((pole) => (
                <option key={pole.id} value={pole.id}>
                  {getPoleLabel(pole)}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Do stĺpika *</label>
            <select
              value={toPole}
              onChange={(e) => handleToPoleChange(parseInt(e.target.value))}
              required
            >
              <option value={0}>Vyberte stĺpik</option>
              {poles.filter(pole => !pole.is_virtual).map((pole) => (
                <option key={pole.id} value={pole.id} disabled={pole.id === fromPole}>
                  {getPoleLabel(pole)}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Dĺžka (metre) *</label>
            <input
              type="number"
              value={length}
              onChange={(e) => setLength(parseInt(e.target.value) || 0)}
              min="0"
              step="0.01"
              required
            />
            {fromPole && toPole && (
              <small className="form-hint">
                Vypočítaná vzdialenosť: {calculateDistance(fromPole, toPole)}m
              </small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="cable-fiber-count">Počet vlákien *</label>
            <FiberCountPicker
              id="cable-fiber-count"
              value={fiberCount}
              onChange={setFiberCount}
              hint={
                (cable || selectedCable) && fiberCount !== povodnyPocetVlakien ? (
                  fiberCount > povodnyPocetVlakien
                    ? `Kábel sa rozšíri z ${povodnyPocetVlakien} na ${fiberCount} vlákien. Doterajšie priradenia zostanú, vlákna navyše budú voľné.`
                    : `Kábel sa zmenší z ${povodnyPocetVlakien} na ${fiberCount} vlákien. Ak je niektoré z odrezaných vlákien obsadené, uloženie sa odmietne.`
                ) : undefined
              }
            />
          </div>

          <div className="form-group">
            <label>Farba na mape</label>
            <div className="farba-kabla-paleta">
              <button
                type="button"
                className={`farba-kabla-vzorka farba-kabla-predvolena ${!mapColor ? 'vybrana' : ''}`}
                onClick={() => setMapColor('')}
                title="Predvolená (tmavá)"
              >
                Predvolená
              </button>
              {FARBY_KABLA.map((f) => (
                <button
                  key={f.hex}
                  type="button"
                  className={`farba-kabla-vzorka ${mapColor.toLowerCase() === f.hex ? 'vybrana' : ''}`}
                  style={{ background: f.hex }}
                  onClick={() => setMapColor(f.hex)}
                  title={f.nazov}
                  aria-label={f.nazov}
                />
              ))}
              <label className="farba-kabla-vlastna" title="Vlastná farba">
                <input
                  type="color"
                  value={mapColor || '#2c3e50'}
                  onChange={(e) => setMapColor(e.target.value)}
                />
                Iná…
              </label>
            </div>
            <small className="form-hint">Len pre lepšiu orientáciu v projekte — napr. každá trasa inou farbou.</small>
          </div>

          <div className="form-group">
            <label>Veľkosť mikrotrubičky</label>
            <select
              value={microtubeSize}
              onChange={(e) => setMicrotubeSize(e.target.value as MicrotubeSize)}
            >
              <option value="">Nešpecifikované</option>
              <option value="7/4">7/4 mm</option>
              <option value="12/10">12/10 mm</option>
              <option value="14/12">14/12 mm</option>
            </select>
          </div>

          <div className="form-group">
            <label>Farba mikrotrubičky</label>
            <select
              value={microtubeColor}
              onChange={(e) => setMicrotubeColor(e.target.value as MicrotubeColor)}
            >
              <option value="">Nešpecifikované</option>
              <option value="yellow">🟡 Žltá</option>
              <option value="red">🔴 Červená</option>
              <option value="green">🟢 Zelená</option>
              <option value="blue">🔵 Modrá</option>
              <option value="purple">🟣 Fialová</option>
              <option value="pink">🩷 Ružová</option>
            </select>
          </div>

          <div className="form-group">
            <label>Typ mikrotrubičky</label>
            <select
              value={microtubeType}
              onChange={(e) => setMicrotubeType(e.target.value as MicrotubeType)}
            >
              <option value="">Nešpecifikované</option>
              <option value="single">Jedna trubička</option>
              <option value="multi-4">Viacnásobná (4 trubičky)</option>
              <option value="multi-7">Viacnásobná (7 trubičiek)</option>
            </select>
          </div>

          <div className="form-group">
            <label>Poznámky</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Voliteľné poznámky..."
              rows={3}
            />
          </div>

          <div className="dialog-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Zrušiť
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (isEditing ? 'Aktualizuje sa...' : 'Vytvára sa...') : (isEditing ? 'Aktualizovať kábel' : 'Vytvoriť kábel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

